import * as React from "react";
import { Link, NavLink } from "react-router";
import {
  Home,
  Building2,
  FileText,
  BookOpen,
  Settings,
  Network,
  FileDown,
  BarChart3,
  MapPin,
  MoreHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/auth-context";
import { isTraegerAdmin } from "@/features/auth/role-utils";
import { ReadOnlyBanner } from "@/features/auth/readonly-banner";

/**
 * AppShell — wrapt alle authenticated routes mit nav + content-area.
 *
 *  - tablet-first (≥768px): side-rail navigation
 *  - mobile (<768px): bottom-tab-bar mit max 5 primary slots + overflow-menu
 *  - non-sticky brand-header mit wordmark links (route → /dashboard)
 *
 * Phase 16.D: TRAEGER_NAV auf 7 items erweitert (+ Vergleich, + Karte).
 * splitNavForMobile() schneidet bei 5 items + "Mehr"-overflow-dropdown.
 */

type NavItem = { to: string; label: string; Icon: typeof Home };

const STANDARD_NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", Icon: Home },
  { to: "/raeume", label: "Räume", Icon: Building2 },
  { to: "/entries/neu", label: "Messung", Icon: FileText },
  { to: "/wiki", label: "Wiki", Icon: BookOpen },
  { to: "/einstellungen", label: "Einstellungen", Icon: Settings },
];

const TRAEGER_NAV: NavItem[] = [
  { to: "/traeger/dashboard", label: "Übersicht", Icon: Home },
  { to: "/traeger/orgs", label: "Einrichtungen", Icon: Network },
  { to: "/traeger/vergleich", label: "Vergleich", Icon: BarChart3 },
  { to: "/traeger/karte", label: "Karte", Icon: MapPin },
  { to: "/traeger/export", label: "Export", Icon: FileDown },
  { to: "/wiki", label: "Wiki", Icon: BookOpen },
  { to: "/einstellungen", label: "Einstellungen", Icon: Settings },
];

const MOBILE_PRIMARY_LIMIT = 5;

export function splitNavForMobile(items: NavItem[]): { primary: NavItem[]; overflow: NavItem[] } {
  if (items.length <= MOBILE_PRIMARY_LIMIT) {
    return { primary: items, overflow: [] };
  }
  // Bei overflow: 4 primary + "Mehr"-slot (5. visual-slot ist der dropdown).
  return {
    primary: items.slice(0, MOBILE_PRIMARY_LIMIT - 1),
    overflow: items.slice(MOBILE_PRIMARY_LIMIT - 1),
  };
}

export function AppShell({ children }: { children: React.ReactNode }): React.ReactElement {
  const auth = useAuth();
  const navItems = isTraegerAdmin(auth.user) ? TRAEGER_NAV : STANDARD_NAV;
  const brandTarget = isTraegerAdmin(auth.user) ? "/traeger/dashboard" : "/dashboard";
  return (
    <div className="flex min-h-screen flex-col bg-[color:var(--color-surface-elevated)] md:flex-row">
      <DesktopRail items={navItems} />
      <main className="flex-1 pb-20 md:pb-0">
        <ReadOnlyBanner />
        <header className="border-b border-[color:var(--color-border)] bg-white px-6 py-4">
          <Link to={brandTarget} className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block size-6 rounded bg-[color:var(--color-brand-primary)]"
            />
            <span className="text-lg font-semibold">HitzeProtokoll</span>
            {isTraegerAdmin(auth.user) && (
              <span className="ml-2 rounded-full bg-[color:var(--color-warm-orange-50)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-warm-orange-900)]">
                Träger
              </span>
            )}
          </Link>
        </header>
        <div className="mx-auto max-w-5xl px-6 py-6">{children}</div>
      </main>
      <MobileTabBar items={navItems} />
    </div>
  );
}

function DesktopRail({ items }: { items: NavItem[] }): React.ReactElement {
  return (
    <nav
      aria-label="Hauptnavigation"
      className="hidden w-60 shrink-0 flex-col gap-1 border-r border-[color:var(--color-border)] bg-white px-3 py-6 md:flex"
    >
      {items.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/dashboard" || to === "/traeger/dashboard"}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-[color:var(--color-brand-primary-soft)] text-[color:var(--color-brand-primary-strong)]"
                : "text-neutral-700 hover:bg-[color:var(--color-surface-sunken)]"
            )
          }
        >
          <Icon aria-hidden="true" className="size-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileTabBar({ items }: { items: NavItem[] }): React.ReactElement {
  const { primary, overflow } = splitNavForMobile(items);
  return (
    <nav
      aria-label="Hauptnavigation"
      className="fixed bottom-0 left-0 right-0 z-[var(--z-sticky)] flex items-stretch border-t border-[color:var(--color-border)] bg-white md:hidden"
    >
      {primary.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/dashboard" || to === "/traeger/dashboard"}
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center justify-center gap-1 px-2 py-2 text-[0.7rem]",
              isActive
                ? "text-[color:var(--color-brand-primary-strong)]"
                : "text-neutral-600"
            )
          }
        >
          <Icon aria-hidden="true" className="size-5" />
          <span className="whitespace-nowrap">{label}</span>
        </NavLink>
      ))}
      {overflow.length > 0 && <OverflowMenu items={overflow} />}
    </nav>
  );
}

function OverflowMenu({ items }: { items: NavItem[] }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", handlePointer);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("pointerdown", handlePointer);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={menuRef} className="relative flex flex-1 flex-col items-center justify-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Weitere Menüpunkte"
        className={cn(
          "flex w-full flex-col items-center justify-center gap-1 px-2 py-2 text-[0.7rem]",
          open ? "text-[color:var(--color-brand-primary-strong)]" : "text-neutral-600",
        )}
      >
        <MoreHorizontal aria-hidden="true" className="size-5" />
        <span>Mehr</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full right-2 mb-2 min-w-[180px] rounded-md border border-[color:var(--color-border)] bg-white py-1 shadow-lg"
        >
          {items.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 text-sm",
                  isActive
                    ? "bg-[color:var(--color-brand-primary-soft)] text-[color:var(--color-brand-primary-strong)]"
                    : "text-neutral-700 hover:bg-[color:var(--color-surface-sunken)]",
                )
              }
            >
              <Icon aria-hidden="true" className="size-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
